import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'

export interface FavoriteItem {
  path: string
  label: string
}

/** Chiave storica, uguale per tutti gli utenti del browser: oggi si usa solo per il passaggio una tantum. */
const CHIAVE_VECCHIA = 'immigreer_favorites'

function chiave(username: string): string {
  return `${CHIAVE_VECCHIA}:${username.toLowerCase()}`
}

/** Un valore riletto dallo storage è dato non fidato: si tengono solo percorsi interni ben formati. */
function pulisci(arr: unknown): FavoriteItem[] {
  if (!Array.isArray(arr)) return []
  return arr.filter(
    (f): f is FavoriteItem =>
      typeof f?.path === 'string' && /^\/(?![/\\])/.test(f.path) && typeof f?.label === 'string',
  )
}

function leggi(k: string): FavoriteItem[] | null {
  try {
    const raw = localStorage.getItem(k)
    return raw == null ? null : pulisci(JSON.parse(raw))
  } catch {
    return null
  }
}

function load(username: string): FavoriteItem[] {
  const propri = leggi(chiave(username))
  if (propri) return propri
  // Primo accesso dopo il passaggio ai preferiti per username: li prende il primo utente che entra,
  // poi la lista vecchia viene tolta, così non finiscono anche al secondo.
  const vecchi = leggi(CHIAVE_VECCHIA)
  if (vecchi) {
    try {
      localStorage.setItem(chiave(username), JSON.stringify(vecchi))
      localStorage.removeItem(CHIAVE_VECCHIA)
    } catch { /* storage non disponibile */ }
    return vecchi
  }
  return []
}

export const useFavoritesStore = defineStore('favorites', () => {
  const auth = useAuthStore()
  const items = ref<FavoriteItem[]>([])
  let username: string | null = null

  // I preferiti seguono l'utente collegato: al cambio utente (o al logout) si ricaricano.
  watch(
    () => auth.me?.username,
    (u) => {
      username = u ?? null
      items.value = u ? load(u) : []
    },
    { immediate: true },
  )

  function save(): void {
    if (!username) return
    try { localStorage.setItem(chiave(username), JSON.stringify(items.value)) } catch { /* ignora */ }
  }

  const isFavorite = computed(() => (path: string) =>
    items.value.some((f) => f.path === path)
  )

  function add(item: FavoriteItem) {
    if (!username) return
    if (!isFavorite.value(item.path)) {
      items.value.push(item)
      save()
    }
  }

  function remove(path: string) {
    items.value = items.value.filter((f) => f.path !== path)
    save()
  }

  function toggle(item: FavoriteItem) {
    if (isFavorite.value(item.path)) {
      remove(item.path)
    } else {
      add(item)
    }
  }

  return { items, isFavorite, add, remove, toggle }
})
